import React, { useState, useEffect, useMemo } from "react";
import { transliterate } from 'transliteration';
import { Tooltip } from "react-tooltip";

import ExpandableTableHeader from "../../components/ExpandableTable/ExpandableTableHeader";
import ExpandableTableBody from "../../components/ExpandableTable/ExpandableTableBody";
import Pagination from "../../components/Pagination/Pagination";
// import Checkbox from "../../components/Checkbox/Checkbox";
import Dropdown from "../../components/Dropdown/Dropdown";
import ExpandableSearch from "../../components/ExpandableSearch/ExpandableSearch";

import "./PointsPredictionList.css";

const PointsPredictionListContainer = (props) => {
    const [mainData, setMainData] = useState([]);
    const [slicedMainData, setSlicedMainData] = useState([]);
    const [activeRows, setActiveRows] = useState([0]);
    const [pageNumber, setPageNumber] = useState(1);
    const [totalPages, setTotalPages] = useState(null);

    const [selectedPosition, setSelectedPosition] = useState(null);
    const [selectedTeam, setSelectedTeam] = useState(null);
    const [selectedMaxPrice, setSelectedMaxPrice] = useState(null);

    const [teamOptions, setTeamOptions] = useState([]);
    const [priceOptions, setPriceOptions] = useState([]);
    const [searchText, setSearchText] = useState('');
    // Set when a player is picked in the Team of the Week: the list shows exactly that player
    // (their name goes in the search box) until the search is changed or cleared.
    const [focusedPlayerId, setFocusedPlayerId] = useState(null);
    const displayNr = 10;

    // Wildcard mode: `data` is one flat list with totals over `wildcardGameweeks`, not a list per gameweek.
    const isWildcard = props.mode === "wildcard";
    const wildcardGameweeks = props.wildcardGameweeks || [];

    const columnNames = {
        "team_id": "",
        "playerName": "Player",
        "status": "",
        "fixture": isWildcard ? "Per GW" : "Fixture",
        "pred": "Points"
    };

    const homeAwayMap = {
        "home": "(H)",
        "away": "(A)"
    }

    const positionMap = {
        1: "GKP",
        2: "DEF",
        3: "MID",
        4: "FWD"
    }

    const columnStyles = {
        width: ["6%", "26%", "8%", "49.2%", "10.8%"],
        minWidth: ["10px", "40px", "5px", "40px", "50px"],
        align: ["center", "left", "left", "left", "center"],
        fontStyle: ["number", "title", "number", "number", "number"],
        icon: ["none", "none", "none", "none", "none"]
    };

    useEffect(() => {
        updateGameweekData(props.gameweek);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.data, props.dataLoading, props.gameweek, props.mode]);

    // Switching between Free Hit and Wildcard starts the list from the top again.
    useEffect(() => {
        setPageNumber(1);
        setActiveRows([0]);
    }, [props.mode]);

    const updateGameweekData = (gw) => {
        const thisGameweekData = isWildcard ? props.data : props.data?.[gw];
        if (!props.dataLoading && thisGameweekData) {
            // Wildcard: every gameweek's points are coloured on one scale over all players and gameweeks
            const gwPoints = isWildcard
                ? thisGameweekData.flatMap(d => wildcardGameweeks.map(w => d["points_by_gw"]?.[w] ?? 0))
                : [];
            const minGw = Math.min(...gwPoints);
            const maxGw = Math.max(...gwPoints);
            const perGwPill = (d) => (
                <span className="per-gw-pill">
                    {wildcardGameweeks.map(w => {
                        const points = d["points_by_gw"]?.[w] ?? 0;
                        const alpha = maxGw === minGw ? 0 : Math.pow((points - minGw) / (maxGw - minGw), 3);
                        return (
                            <span key={w}
                                  data-tooltip-id="per-gw-fixture-tooltip"
                                  data-tooltip-content={props.fixturesById?.[d["id"]]?.[w] || "No fixture"}
                                  style={{background: `rgba(23, 89, 128, ${alpha})`, color: alpha > 0.65 ? 'white' : 'inherit'}}>
                                {points.toFixed(1)}
                            </span>
                        );
                    })}
                </span>
            );

            let data = [...thisGameweekData].sort((a,b) => (b["total_points"] - a["total_points"]) || a["web_name"].localeCompare(b["web_name"])).map((d, idx) => {
                const minutesPoints = Array.isArray(d["points_minutes"]) ? d["points_minutes"] : [d["points_minutes"]];
                const appearancePoints = isWildcard
                    ? [wildcardGameweeks.reduce((sum, w) => sum + (props.appearancesByIdAndGameweek?.[d["id"]]?.[w] ?? 0), 0)]
                    : minutesPoints.map(val => val > 0 ? 1 : 0);
                const sixtyMinutesPoints = minutesPoints.map((val, idx) => val - appearancePoints[idx]);
                return {
                    "data": d["data"],
                    "player_id": d['code'],
                    "team_id": d["team_code"],
                    "team_name": d["team_name"],
                    "player_position": positionMap[d['element_type']],
                    "price": d["now_cost"]/10,
                    "playerName": d["web_name"],
                    // The wildcard list has no status, so it is taken from the upcoming gameweek.
                    "status": d['status'] ?? props.statusById?.[d['id']],
                    "fixture": isWildcard
                            ? perGwPill(d)
                            : Array.isArray(d["opponent_name"])
                            ? d["opponent_name"].map((opp, idx) => opp + homeAwayMap[d["home_away"][idx]]).join(',')
                            : d["opponent_name"] + homeAwayMap[d["home_away"]],
                    "pred": d["total_points"],
                    // Totals over several gameweeks: chances add up to expected counts, not percentages.
                    "multi_gw": isWildcard,
                    "points_goals": Array.isArray(d["points_goals"]) ? d["points_goals"] : [d["points_goals"]],
                    // Added in data 0.1.3, and absent from gameweeks published before it.
                    "points_penalty_goals": Array.isArray(d["points_penalty_goals"])
                            ? d["points_penalty_goals"]
                            : [d["points_penalty_goals"] ?? 0],
                    "points_assists": Array.isArray(d["points_assists"]) ? d["points_assists"] : [d["points_assists"]],
                    "points_clean_sheet": Array.isArray(d["points_clean_sheet"]) ? d["points_clean_sheet"] : [d["points_clean_sheet"]],
                    "points_defcon": Array.isArray(d["points_defcon"]) ? d["points_defcon"] : [d["points_defcon"]],
                    "points_bps": Array.isArray(d["points_bps"]) ? d["points_bps"] : [d["points_bps"]],
                    "points_starts": appearancePoints,
                    "points_minutes": sixtyMinutesPoints,
                    "points_saves": Array.isArray(d["points_saves"]) ? d["points_saves"] : [d["points_saves"]],
                    "points_pen_saves":Array.isArray(d["points_pen_saves"]) ? d["points_pen_saves"] : [d["points_pen_saves"]],
                    "points_goals_conceded": Array.isArray(d["points_goals_conceded"]) ? d["points_goals_conceded"] : [d["points_goals_conceded"]],
                    "points_negative": Array.isArray(d["opponent_name"])
                            ? d["opponent_name"].map((opp, idx) => (
                                d['points_own_goal'][idx] +
                                d['points_pen_missed'][idx] +
                                d['points_yellow_card'][idx] +
                                d['points_red_card'][idx] +
                                d['points_goals_conceded'][idx]
                            ))
                            : [
                                d['points_own_goal'] +
                                d['points_pen_missed'] +
                                d['points_yellow_card'] +
                                d['points_red_card'] +
                                d['points_goals_conceded']
                            ]
                }
            });
            const predValues = data.map(d => d.pred);
            const minPred = Math.min(...predValues);
            const maxPred = Math.max(...predValues);

            data = data.map(d => ({
                ...d,
                'alpha': maxPred === minPred ? 0 : Math.pow((d.pred - minPred) / (maxPred - minPred), 3)
            }));

            setMainData(data);

            const uniqueTeamsMap = new Map();
            data.forEach(d => {
                if (!uniqueTeamsMap.has(d.team_name)) {
                    uniqueTeamsMap.set(d.team_name, d.team_id);
                }
            });

            const teamOptions = Array.from(uniqueTeamsMap.entries())
                .sort((a, b) => a[0].localeCompare(b[0]))
                .map(([teamName, teamId]) => ({
                    value: teamName,
                    label: teamName,
                    team_id: teamId
                }));

            setTeamOptions(teamOptions);

            const allPrices = data.map(d => d.price);
            const maxPrice = Math.max(...allPrices);
            const minPrice = Math.min(...allPrices);
            const pOptions = [];

            for (let i = maxPrice; i >= minPrice - 0.05; i -= 0.1) {
                pOptions.push({
                    value: parseFloat(i.toFixed(1)),
                    label: i.toFixed(1)
                });
            }
            setPriceOptions(pOptions);

            let dataLength = data.length;
            let numPages = Math.ceil(dataLength / displayNr);
            setTotalPages(numPages);

            let slicedData = data.slice(0, displayNr);
            setSlicedMainData(slicedData);
        }
    };

    useEffect(() => {
        let dataToSlice = mainData

        if (selectedTeam) {
            dataToSlice = dataToSlice.filter(item => item.team_name === selectedTeam.value);
        }

        if (selectedPosition) {
            dataToSlice = dataToSlice.filter(item => item.player_position === selectedPosition.value);
        }

        if (selectedMaxPrice && selectedMaxPrice.value !== 999) {
            dataToSlice = dataToSlice.filter(item => item.price <= selectedMaxPrice.value);
        }

        if (focusedPlayerId != null) {
            dataToSlice = dataToSlice.filter(item => item.player_id === focusedPlayerId);
        } else if (searchText) {
            const normalizedSearchText = normalizeForSearch(searchText.toLowerCase());
            dataToSlice = dataToSlice.filter(item =>
                normalizeForSearch(item.playerName.toLowerCase()).includes(normalizedSearchText)
            );
            setActiveRows([]);
        }

        const numPages = Math.ceil(dataToSlice.length / displayNr);
        setTotalPages(numPages);

        if (pageNumber > numPages && numPages > 0) {
            setPageNumber(1);
        }

        const slicedData = dataToSlice.slice((pageNumber - 1) * displayNr, pageNumber * displayNr);
        setSlicedMainData(slicedData);
    }, [mainData, pageNumber, displayNr, selectedTeam, selectedPosition, selectedMaxPrice, searchText, focusedPlayerId]);

    // A player picked in the Team of the Week: show only them, with their breakdown open
    useEffect(() => {
        if (!props.focusedPlayer) return;
        setFocusedPlayerId(props.focusedPlayer.code);
        setSearchText(props.focusedPlayer.name);
        setSelectedTeam(null);
        setSelectedPosition(null);
        setSelectedMaxPrice(null);
        setPageNumber(1);
        setActiveRows([0]);
    }, [props.focusedPlayer]);

    const searchHandler = (text) => {
        setFocusedPlayerId(null);
        setSearchText(text);
    };

    const paginationHandler = (page) => {
        setPageNumber(page);
        setActiveRows([]);
    };

    const teamSelectionHandler = (selectedOption) => {
        setSelectedTeam(selectedOption);
        setPageNumber(1);
        setActiveRows([0]);
    };

    const positionSelectionHandler = (selectedOption) => {
        setSelectedPosition(selectedOption);
        setPageNumber(1);
        setActiveRows([0]);
    };

    const priceSelectionHandler = (selectedOption) => {
        setSelectedMaxPrice(selectedOption);
        setPageNumber(1);
        setActiveRows([0]);
    };

    const normalizeForSearch = (str) => {
        return transliterate(str).toLowerCase();
    };

  const table = (
      <div style={{display: "flex", flexDirection: "column", alignItems: "center", width: "100%"}}>
          <div className={"title-container noSelect"}>
              <div className={"font-totw-title"}>Predicted Points</div>
              <div></div>
              {isWildcard && wildcardGameweeks.length > 0
                  ? <div className={"font-totw-title"}>GW{wildcardGameweeks[0]}–{wildcardGameweeks[wildcardGameweeks.length - 1]}</div>
                  : <div></div>}
          </div>
          <div className={"player-list-container noSelect"}>
              <div className={"selection-container noSelect"}>
                  <Dropdown
                      style={{minWidth: '100px'}}
                      options={teamOptions}
                      value={selectedTeam}
                      onChange={teamSelectionHandler}
                      placeholder="Team"
                      isSearchable={false}
                  />
                  <Dropdown
                      style={{minWidth: "125px"}}
                      options={[
                          {value: 'GKP', label: 'Goalkeeper'},
                          {value: 'DEF', label: 'Defender'},
                          {value: 'MID', label: 'Midfielder'},
                          {value: 'FWD', label: 'Forward'}
                      ]}
                      value={selectedPosition}
                      onChange={positionSelectionHandler}
                      placeholder="Position"
                      isSearchable={false}
                  />
                  <Dropdown
                      style={{ minWidth: "82px" }}
                      options={priceOptions}
                      value={selectedMaxPrice}
                      onChange={priceSelectionHandler}
                      placeholder="Budget"
                      isSearchable={false}
                  />
                  <ExpandableSearch
                      value={searchText}
                      onChange={searchHandler}
                      placeholder="Search players..."
                  />
                  {/*<div className={"options-container noSelect"}>*/}
                  {/*    <div className={"font-number-capital"}>Show red flags</div>*/}
                  {/*    <Checkbox*/}
                  {/*        checked={showRedFlags}*/}
                  {/*        checkBoxHandler={redFlagCheckboxHandler}*/}
                  {/*    />*/}
                  {/*</div>*/}
              </div>
              <div className="player-list-table">
                  <ExpandableTableHeader
                  headers={columnNames}
                  headerInfo={columnStyles}
                  onClick={[]}
                  excludedColumns={[]}
              />
              <ExpandableTableBody
                  data={slicedMainData}
                  rowItemNames={columnNames}
                  rowItemInfo={columnStyles}
                  firstRowActive={true}
                  activeRows={activeRows}
                  setActiveRows={setActiveRows}
                  expandedAreaType={"prediction"}
                  hiddenColumns={[]}
                  clickedHeader={[]}
                  excludedColumns={[]}
                  alternatingRows={false}
              />
              </div>

          </div>
          {isWildcard && <Tooltip id="per-gw-fixture-tooltip" className="my-tooltip"/>}
          <Pagination
              totalPages={totalPages}
              currentPage={pageNumber}
              handlePaginationClick={paginationHandler}
          />
      </div>
  );

    return table;
};

export default PointsPredictionListContainer;
